import { useEffect, RefObject, MutableRefObject } from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import * as turf from '@turf/turf'
import { GeoAnalysisSectionLand } from '../../GeoAnalysisSection'
import type { GeoAnalysisSectionTranslations } from '../../GeoAnalysisSection.types'

interface UsePolygonChartParams {
  chartRef: RefObject<HTMLDivElement>
  activeTab: string
  land: GeoAnalysisSectionLand | null | undefined
  translations?: GeoAnalysisSectionTranslations
  chartInstanceRef?: MutableRefObject<echarts.ECharts | null>
}

/**
 * Hook to manage 2D polygon boundary visualization chart lifecycle
 * Shows plot boundaries with distance labels between points
 */
export const usePolygonChart = ({
  chartRef,
  activeTab,
  land,
  translations,
  chartInstanceRef,
}: UsePolygonChartParams) => {
  useEffect(() => {
    if (activeTab !== 'shape' || !chartRef.current) return

    const chart = safeChartInit(chartRef.current)
    if (chartInstanceRef) chartInstanceRef.current = chart

    // Chart labels with translations
    const distanceLabel = translations?.charts?.distance || 'Distance'
    const plotBoundaryLabel = translations?.charts?.plotBoundary || 'Plot boundary'

    // Create polygon visualization
    // Unwrap Feature to raw geometry if needed
    const geoPolygon =
      land?.geo_polygon?.type === 'Feature' ? land.geo_polygon.geometry : land?.geo_polygon

    let polygonData: number[][] = []
    if (geoPolygon) {
      // Handle MultiPolygon structure
      if (
        geoPolygon.type === 'MultiPolygon' &&
        Array.isArray(geoPolygon.coordinates) &&
        geoPolygon.coordinates.length > 0 &&
        Array.isArray(geoPolygon.coordinates[0]) &&
        geoPolygon.coordinates[0].length > 0 &&
        Array.isArray(geoPolygon.coordinates[0][0])
      ) {
        // Extract the first polygon's first ring from MultiPolygon
        polygonData = geoPolygon.coordinates[0][0].map((coord: number[]) => [coord[0], coord[1]])
      } else if (
        geoPolygon.type === 'Polygon' &&
        Array.isArray(geoPolygon.coordinates) &&
        geoPolygon.coordinates.length > 0 &&
        Array.isArray(geoPolygon.coordinates[0])
      ) {
        // Extract the first ring from Polygon
        polygonData = geoPolygon.coordinates[0].map((coord: number[]) => [coord[0], coord[1]])
      }
    }

    // Skip visualization if no valid polygon data
    if (polygonData.length === 0) {
      chart.dispose()
      return
    }

    // Calculate distances between consecutive points
    const distanceLabels: any[] = []
    for (let i = 0; i < polygonData.length - 1; i++) {
      const point1 = turf.point(polygonData[i])
      const point2 = turf.point(polygonData[i + 1])
      const distance = turf.distance(point1, point2, { units: 'meters' })

      // Calculate midpoint for label placement
      const midLon = (polygonData[i][0] + polygonData[i + 1][0]) / 2
      const midLat = (polygonData[i][1] + polygonData[i + 1][1]) / 2

      distanceLabels.push({
        coord: [midLon, midLat],
        distance: distance.toFixed(1),
        value: [midLon, midLat],
      })
    }

    const option = {
      animation: false,
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          if (params.seriesType === 'scatter' && params.seriesIndex === 1) {
            return `${distanceLabel}: ${params.data.distance} m`
          }
          return plotBoundaryLabel
        },
      },
      geo: {
        map: 'plot',
        roam: true,
        silent: false,
        itemStyle: {
          areaColor: '#00B341',
          borderColor: '#0294df',
          borderWidth: 2,
        },
        emphasis: {
          itemStyle: {
            areaColor: '#0294df',
          },
        },
      },
      series: [
        {
          type: 'scatter',
          coordinateSystem: 'geo',
          data: polygonData.map((coord: number[]) => ({
            value: coord,
            itemStyle: { color: '#0294df' },
          })),
          symbolSize: 8,
          z: 2,
        },
        {
          type: 'scatter',
          coordinateSystem: 'geo',
          data: distanceLabels,
          symbolSize: 0,
          label: {
            show: true,
            formatter: (params: any) => `${params.data.distance}m`,
            position: 'top',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderColor: '#999',
            borderWidth: 1,
            padding: [2, 4],
            borderRadius: 2,
            fontSize: 11,
            color: '#333',
          },
          z: 3,
        },
      ],
    }

    // Register custom map
    echarts.registerMap('plot', {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [polygonData],
          },
          properties: { name: 'Plot' },
        },
      ],
    })

    chart.setOption(option)

    // Cleanup
    return () => {
      if (chartInstanceRef) chartInstanceRef.current = null
      if (chart && !chart.isDisposed()) {
        chart.dispose()
      }
    }
  }, [activeTab, chartRef, land, translations, chartInstanceRef])
}
